import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Home, BookOpen, User, Calendar, Star, Loader2 } from 'lucide-react';

const MyStory = () => {
  const [activeTab, setActiveTab] = useState('story');
  const [predictionStatus, setPredictionStatus] = useState('loading'); // loading, ready, playing, error
  const [prediction, setPrediction] = useState('');
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);
  
  useEffect(() => {
    checkPredictionStatus();
    const interval = setInterval(checkPredictionStatus, 3000); // Poll every 3s
    return () => clearInterval(interval);
  }, []);
  
  const checkPredictionStatus = async () => {
    const predictionId = localStorage.getItem('prediction_id');
    if (!predictionId) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';
      const response = await fetch(`${backendUrl}/api/story/status/${predictionId}`);
      const data = await response.json();
      
      if (data.status === 'completed') {
        setPredictionStatus('ready');
        setPrediction(data.prediction);
      } else if (data.status === 'error') {
        setPredictionStatus('error');
      } else {
        setProgress(data.progress || 0);
      }
    } catch (err) {
      console.error('Status check failed:', err);
    }
  };
  
  const playStory = () => {
    setPredictionStatus('playing');
    // In production: Play TTS audio
    setTimeout(() => setPredictionStatus('ready'), 3000);
  };

  const lifeEvents = [
    { year: 2020, event: 'Career Milestone', description: 'Major promotion', domain: 'career', icon: '💼' },
    { year: 2022, event: 'Personal Growth', description: 'Spiritual awakening', domain: 'spirituality', icon: '🕊' },
    { year: 2024, event: 'Health Achievement', description: 'Wellness journey', domain: 'health', icon: '💚' },
  ];

  return (
    <div className="min-h-screen pb-24" data-testid="my-story-screen">
      {/* Sage Avatar Section */}
      {predictionStatus === 'loading' && (
        <div className="p-6 mb-6">
          <div className="card-astro text-center py-8">
            <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-gradient-to-br from-gold/20 to-purple-500/20 flex items-center justify-center">
              <Loader2 className="w-16 h-16 text-gold animate-spin" />
            </div>
            <h3 className="text-xl font-semibold mb-2" style={{color: 'var(--gold)'}}>
              🧙‍♂️ The Sage is Analyzing...
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              Creating your personalized story
            </p>
            <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
              <div 
                className="bg-gradient-to-r from-gold to-purple-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-gray-500 text-xs">{progress}% complete</p>
          </div>
        </div>
      )}
      
      {predictionStatus === 'ready' && (
        <div className="p-6 mb-6">
          <div className="card-astro text-center py-8" style={{background: 'linear-gradient(135deg, rgba(212,175,55,0.1), rgba(138,43,226,0.1))'}}>
            <div className="w-40 h-40 mx-auto mb-4 rounded-full bg-gradient-to-br from-gold to-purple-500 flex items-center justify-center text-7xl animate-pulse">
              🧙‍♂️
            </div>
            <h3 className="text-2xl font-bold mb-3" style={{fontFamily: 'Playfair Display', color: 'var(--gold)'}}>
              Your Story is Ready
            </h3>
            <p className="text-gray-400 text-sm mb-6 px-4">
              The Sage has analyzed your past and prepared your story
            </p>
            <button
              onClick={playStory}
              className="px-8 py-3 rounded-full font-semibold transition-all shadow-lg"
              style={{
                background: 'linear-gradient(135deg, var(--gold), #FFD700)',
                color: '#1a0b2e'
              }}
            >
              🎙️ Play My Story
            </button>
          </div>
        </div>
      )}
      
      {predictionStatus === 'playing' && (
        <div className="p-6 mb-6">
          <div className="card-astro text-center py-8">
            <div className="w-40 h-40 mx-auto mb-4 rounded-full bg-gradient-to-br from-gold to-purple-500 flex items-center justify-center text-7xl"
                 style={{animation: 'pulse 1.5s ease-in-out infinite'}}>
              🧙‍♂️
            </div>
            <h3 className="text-2xl font-bold mb-3" style={{fontFamily: 'Playfair Display', color: 'var(--gold)'}}>
              The Sage Speaks...
            </h3>
            <div className="flex justify-center gap-2 mb-4">
              <div className="w-2 h-8 bg-gold rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
              <div className="w-2 h-12 bg-gold rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
              <div className="w-2 h-10 bg-gold rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
              <div className="w-2 h-12 bg-gold rounded-full animate-bounce" style={{animationDelay: '450ms'}}></div>
              <div className="w-2 h-8 bg-gold rounded-full animate-bounce" style={{animationDelay: '600ms'}}></div>
            </div>
            <p className="text-gray-400 text-sm px-4 leading-relaxed">
              {prediction.substring(0, 200)}...
            </p>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="p-6 pb-8">
        <h1 className="text-3xl mb-2" style={{fontFamily: 'Playfair Display', color: 'var(--gold)'}} data-testid="page-title">
          My Story
        </h1>
        <p className="text-gray-400">Your cosmic journey through time</p>
      </div>

      {/* Timeline Visualization */}
      <div className="px-6 mb-8">
        <div className="relative">
          {/* Vertical Line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-gold to-transparent" />

          {/* Events */}
          <div className="space-y-8">
            {lifeEvents.map((event, idx) => (
              <div key={idx} className="relative pl-20" data-testid={`event-${idx}`}>
                {/* Year Marker */}
                <div className="absolute left-0 w-16 h-16 rounded-full border-2 border-gold flex items-center justify-center" style={{background: 'rgba(212, 175, 55, 0.1)'}}>
                  <span className="text-2xl">{event.icon}</span>
                </div>

                {/* Event Card */}
                <div className="card-astro">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold" style={{color: 'var(--gold)'}}>{event.event}</h3>
                    <span className="text-sm text-gray-500">{event.year}</span>
                  </div>
                  <p className="text-gray-400 text-sm">{event.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Insights Section */}
      <div className="px-6">
        <h2 className="text-xl mb-4" style={{fontFamily: 'Playfair Display', color: 'var(--gold)'}}>Key Insights</h2>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="card-astro text-center">
            <Calendar className="w-8 h-8 mx-auto mb-2" style={{color: 'var(--gold)'}} />
            <p className="text-2xl font-bold mb-1" style={{color: 'var(--gold)'}}>12</p>
            <p className="text-sm text-gray-400">Major Events</p>
          </div>

          <div className="card-astro text-center">
            <Star className="w-8 h-8 mx-auto mb-2" style={{color: 'var(--gold)'}} />
            <p className="text-2xl font-bold mb-1" style={{color: 'var(--gold)'}}>87%</p>
            <p className="text-sm text-gray-400">Accuracy</p>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <nav className="bottom-nav">
        <Link to="/dashboard" className={`bottom-nav-item ${activeTab === 'home' ? 'active' : ''}`} data-testid="nav-home">
          <Home className="w-6 h-6" />
          <span>Home</span>
        </Link>
        <Link to="/my-story" className={`bottom-nav-item ${activeTab === 'story' ? 'active' : ''}`} data-testid="nav-story">
          <BookOpen className="w-6 h-6" />
          <span>My Story</span>
        </Link>
        <Link to="/profile" className={`bottom-nav-item ${activeTab === 'profile' ? 'active' : ''}`} data-testid="nav-profile">
          <User className="w-6 h-6" />
          <span>Profile</span>
        </Link>
      </nav>
    </div>
  );
};

export default MyStory;
